import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { trackSignupIntent } from '@/lib/tracking';

interface SolutionHeroProps {
  badge: string;
  title: React.ReactNode;
  subtitle: string;
  benefits: string[];
  ctaText?: string;
  image?: React.ReactNode; // Optional custom graphic per page
}

export const SolutionHero: React.FC<SolutionHeroProps> = ({ badge, title, subtitle, benefits, ctaText = "Start Free Trial", image }) => {
  const appSignUpUrl = import.meta.env.VITE_APP_URL 
    ? `${import.meta.env.VITE_APP_URL}/auth?mode=signup`
    : 'http://localhost:3000/auth?mode=signup';

  return (
    <section className="relative pt-32 pb-24 md:pt-48 md:pb-32 overflow-hidden bg-background">
      {/* Abstract Background */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
         <div className="absolute top-[-20%] right-[-10%] w-[60vw] h-[60vw] bg-primary/10 rounded-full blur-[120px] animate-float opacity-60" />
      </div>

      <div className="container px-4">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          
          {/* Content */}
          <div className="flex-1 text-center lg:text-left">
            <Badge variant="outline" className="mb-6 py-1.5 px-4 text-sm border-primary/20 bg-primary/5 text-primary font-medium rounded-full animate-enter">
              {badge}
            </Badge>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 text-foreground text-balance animate-enter delay-100">
              {title}
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0 animate-enter delay-200">
              {subtitle}
            </p>

            <div className="flex flex-wrap gap-4 justify-center lg:justify-start mb-10 animate-enter delay-300">
              {benefits.map((benefit, i) => (
                <div key={i} className="flex items-center gap-2 text-sm font-medium text-foreground/80">
                  <CheckCircle2 size={18} className="text-green-500" />
                  {benefit}
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-enter delay-300">
              <Button size="lg" className="h-14 px-8 rounded-full text-lg shadow-lg hover:scale-105 transition-transform" asChild>
                <a href={appSignUpUrl} onClick={() => trackSignupIntent('solution_hero')}>{ctaText} <ArrowRight className="ml-2 h-5 w-5" /></a>
              </Button>
            </div>
          </div>

          {/* Visual/Graphic */}
          <div className="flex-1 w-full max-w-xl lg:max-w-none animate-enter delay-500 perspective-1000">
            {image ? image : (
               <div className="aspect-[4/3] bg-muted/20 rounded-3xl border border-border/50 flex items-center justify-center relative overflow-hidden group">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
                  <div className="text-muted-foreground">High-Fi Mockup Placeholder</div>
               </div>
            )}
          </div>

        </div>
      </div>
    </section>
  );
};
